import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { RHFTextField } from 'src/components/hook-form';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { getProduct, updateProduct } from 'src/services/products.service';
import { useParams } from 'src/routes/hooks';

export default function EditProductView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const schema = Yup.object().shape({
    product_name: Yup.string().required(),
    product_code: Yup.string().nullable(),
    description: Yup.string().nullable(),
    price: Yup.number().min(0).required(),
    quantity: Yup.string().nullable(),
    category: Yup.string().nullable(),
    best_before: Yup.date().nullable(),
    is_active: Yup.boolean(),
    prescription_required: Yup.boolean(),
    image_url: Yup.array().of(Yup.string().nullable()),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      product_name: '',
      product_code: '',
      description: '',
      price: null,
      quantity: '',
      category: '',
      best_before: null,
      is_active: true,
      prescription_required: false,
      image_url: [],
    },
  });

  const { reset, getValues, watch, setValue } = methods;
  const productImages = watch('image_url');
  const fileName = `product_image_${new Date().toISOString()}`;
  const folderName = `images/products/${user.clinic_id}`;

  useEffect(() => {
    const load = async () => {
      try {
        const product = await getProduct(productId, user.clinic_id);
        reset({
          product_name: product.product_name || '',
          product_code: product.product_code || '',
          description: product.description || '',
          price: product.price || null,
          quantity: product.quantity || '',
          category: product.category || '',
          best_before: product.best_before ? product.best_before.split('T')[0] : null,
          is_active: product.is_active !== undefined ? product.is_active : true,
          prescription_required: product.prescription_required || false,
          image_url: product.image_url || [],
        });
      } catch {
        setSnackbarMessage('Failed to load product');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productId, reset, user.clinic_id]);

  const handleSave = async () => {
    const data = getValues();
    const payload = {
      ...data,
      clinic_id: user.clinic_id,
      image_url: Array.isArray(data.image_url) ? data.image_url : [data.image_url],
    };

    try {
      await updateProduct(productId, payload,user.clinic_id);
      setSnackbarMessage('Product updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate(-1);
    } catch (e) {
      setSnackbarMessage(e.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ maxWidth: 840, mt: 4 }}>
      <Stack direction="row" alignItems="center" mb={2} gap={2}>
        <IconButton onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Edit Product</Typography>
      </Stack>

      <FormProvider {...methods}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="product_name" label="Product Name" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="product_code" label="Product Code (Optional)" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="price" label="Price" type="number" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="quantity" label="Quantity" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="category" label="Category" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <RHFTextField
              name="best_before"
              label="Best Before"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField name="description" label="Description" multiline rows={4} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={watch('is_active')}
                  onChange={(e) => setValue('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={watch('prescription_required')}
                  onChange={(e) => setValue('prescription_required', e.target.checked)}
                />
              }
              label="Prescription Required"
            />
          </Grid>
          <Grid item xs={12}>
            <RHFImageUpload
              initialImage={productImages}
              fileName={fileName}
              setValue={setValue}
              name="image_url"
              folderName={folderName}
              displayImages
              multipleFiles
              maxFiles={5}
              label="Product Images"
              width={300}
              height={200}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{ background: '#D92D7F', ':hover': { background: '#D92D7F' } }}
              onClick={handleSave}
            >
              Save Product
            </Button>
          </Grid>
        </Grid>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
